import React, {
  useEffect,
  useRef,
  ReactNode,
  CSSProperties,
  useCallback
} from 'react'
import { classNames } from '@/util/tailwindHelpers'
import ReactModal from 'react-modal'
import styles from './styles.module.scss'
import { useWindowHeight } from '@/contexts/windowDimensions'
import { designSystem } from '@/util/tailwindHelpers'
import { ModalTheme } from '@/types/modal'
import ModalHeaderMedia from './ModalHeader'
import { isHeaderObject } from './util'
import Header from './components/Header'
import ModalBody from './components/Body'
import ModalFooter from './components/Footer'
import Overlay from './components/Overlay'
import { ModalChildren, ModalChildrenWrappers, ModalTitleProps } from './types'

export const BASE_MODAL_SPACING = 24
export const MODAL_CLOSE_BUTTON_WIDTH = 24
export const MODAL_TRANSITION_LENGTH_MS = 350
export const MODAL_OVERLAY_ID = 'ModalOverlay'

export interface IModal {
  className?: string
  isCancelDisabled?: boolean
  isCloseDisabled?: boolean
  shouldHideHeader?: boolean
  headerRightSlot?: ReactNode
  spacing?: number
  theme?: ModalTheme
  onAfterClose?: () => void
  hide: () => void
  isOpen: boolean
  onRetreat?: () => void // when user wants to go back to previous view
  onCancel?: () => void // when user wants to cancel modal and potentially a flow
  shouldBlockActions?: boolean
  overlayClassName?: string
  titleClassName?: string
  children?: ModalChildren
  title?: ReactNode | ModalTitleProps
  subFooter?: ReactNode
  overlayHeaderOpaqueOnScroll?: boolean
  renderInlineTitle?: boolean
  viewId?: string
}

const Modal = ({
  className = '',
  theme = 'dark',
  shouldHideHeader = false,
  overlayHeaderOpaqueOnScroll = false,
  headerRightSlot,
  hide,
  onAfterClose,
  onRetreat,
  onCancel,
  shouldBlockActions = false,
  isCancelDisabled = false,
  isCloseDisabled = false,
  isOpen = false,
  children,
  overlayClassName = '',
  spacing = BASE_MODAL_SPACING,
  title,
  titleClassName,
  viewId = null
}: IModal) => {
  const windowHeight = useWindowHeight()
  const bodyTitle = isHeaderObject(title) ? title.body : title

  // Refs /////////////////////////////////////////////////////////////////

  const overlayRef = useRef<HTMLDivElement | null>(null)
  const headerHeightRef = useRef<number>(0)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const scrollbarWidth = useRef<number>(0)

  // Helpers /////////////////////////////////////////////////////////////

  const getScrollbarWidth = (): number =>
    contentRef.current.parentElement.offsetWidth -
    contentRef.current.parentElement.clientWidth

  // Lifecycle ///////////////////////////////////////////////////////////

  useEffect(() => {
    if (!contentRef.current) return
    const resizeObserver = new ResizeObserver(() => {
      const isContentTallerThanWindow =
        contentRef.current?.offsetHeight > windowHeight - spacing * 2

      if (isContentTallerThanWindow) {
        scrollbarWidth.current = getScrollbarWidth()
      } else if (!isContentTallerThanWindow) {
        scrollbarWidth.current = getScrollbarWidth()
      }
    })
    resizeObserver.observe(contentRef.current)
    return () => resizeObserver.disconnect() // clean up
  }, [contentRef.current])

  useEffect(() => {
    resetScroll()
  }, [viewId])

  // Event Handlers & Helpers ////////////////////////////////////////////

  const handleOnCancel = () => {
    onCancel?.()
    hide()
  }

  const resetScroll = () => {
    if (contentRef.current?.parentElement) {
      contentRef.current.parentElement.scroll({ top: 0 })
    }
  }
  const ModalBodyCallback = useCallback(
    ({ className, children }) => (
      <ModalBody
        className={className}
        title={bodyTitle}
        titleClassName={titleClassName}
      >
        {children}
      </ModalBody>
    ),
    [viewId, bodyTitle]
  )

  // Render //////////////////////////////////////////////////////////////////////

  return (
    <ReactModal
      id="Modal"
      style={{
        overlay: {
          '--modalPadding': `${spacing}px`,
          '--modalBodyBottomPadding': `${spacing}px`,
          '--headerHeight': `${headerHeightRef.current}px`,
          '--maxWidth': '480px',
          '--modalTransitionInMs': MODAL_TRANSITION_LENGTH_MS,
          '--scrollbarWidth': `${scrollbarWidth.current}px`,
          '--closeButtonWidth': `${MODAL_CLOSE_BUTTON_WIDTH}px`,
          '--closeButtonTotalWidth': `calc(calc(${spacing}px * 2) + ${MODAL_CLOSE_BUTTON_WIDTH}px)`,
          '--bgColor': `${designSystem.colors['gray-850']}`,
          '--borderColor': `${designSystem.colors['gray-900']}`
        } as CSSProperties
      }}
      shouldCloseOnEsc={!(isCancelDisabled || isCloseDisabled)}
      shouldCloseOnOverlayClick={!(isCancelDisabled || isCloseDisabled)}
      contentRef={ref => {
        contentRef.current = ref
      }}
      overlayElement={(props, contentElement) => (
        <Overlay {...props} ref={overlayRef} id={MODAL_OVERLAY_ID}>
          {contentElement}
        </Overlay>
      )}
      closeTimeoutMS={MODAL_TRANSITION_LENGTH_MS}
      onAfterOpen={e => e.contentEl.focus()}
      onAfterClose={onAfterClose}
      isOpen={isOpen}
      className={classNames(className, styles.modal)}
      overlayClassName={classNames(styles.overlay, overlayClassName, {
        'pointer-events-none': shouldBlockActions
      })}
      onRequestClose={handleOnCancel}
    >
      {!shouldHideHeader && (
        <Header
          headerHeightRef={headerHeightRef}
          overlayRef={overlayRef}
          spacing={spacing}
          title={title}
          theme={theme}
          rightSlot={headerRightSlot}
          onRetreat={onRetreat}
          isCloseDisabled={isCloseDisabled}
          onCancel={onCancel}
          hide={hide}
        />
      )}

      {/* Body */}

      <main
        className={classNames(styles.content, {
          [styles.overlayHeader]: overlayHeaderOpaqueOnScroll
        })}
      >
        {children({
          ModalBody: ModalBodyCallback,
          ModalFooter,
          ModalHeaderMedia
        } as ModalChildrenWrappers)}
      </main>
    </ReactModal>
  )
}

export default Modal
